import { useEffect, useState } from 'react'
import { Button as ButtonComponent } from '@/components/ui/button'
import { CubeLoader as CubeLoaderComponent } from '@/components/ui/cube-loader'
import WizardSteps from '@/components/common/wizard/WizardSteps'
import { useWizard } from '@/components/mediaBuying/adCreatives/AdCreativeWizard'
import { AdCreativeAsset, ButtonProps, CubeLoaderProps } from '@/types'
import VideoThumbnailEditor from '@/components/mediaBuying/adCreatives/shared/VideoThumbnailEditor'
import { useQuery } from '@apollo/client'
import { GET_AD_ASSET_BY_ID, GET_AD_ASSETS } from '@/graphql/ads/ad-assets-queries'
import { useThumbnailEditor } from '@/components/mediaBuying/adCreatives/screens/carouselCards/hooks/useThumbnailEditor'
import { LoaderCircle } from 'lucide-react'
import { showNotification } from '@/utils/notifications'

interface WizardContextType {
    id: string
    campaignId: string
    campaignFormat: string
    getStepContentData: (stepName?: string) => Record<string, any>
    goToStep: (stepName: string) => void
    setStepContentData: (dataUpdate: Record<string, any>, stepName?: string) => void
    registerCustomNavigation: (navigationComponent: React.ReactNode) => void
    clearCustomNavigation: () => void
    currentStep: number
    stepOrder: string[]
    dataForStepper: Record<string, any>
}

interface ThumbnailSelection {
    mode: string
    firstThumbnail?: { id: number } | null
    selectedThumbnail?: { id: number } | null
}

const Button = ButtonComponent as React.FC<ButtonProps>
const CubeLoader = CubeLoaderComponent as React.FC<CubeLoaderProps>

const Navigation = ({
    goBack,
    goNext,
    isUploading,
    isUpdating,
}: {
    goBack: () => void
    goNext: () => void
    isUploading: boolean
    isUpdating: boolean
}) => {
    return (
        <div className="d-flex gap-2">
            <Button variant="secondary" onClick={goBack} className="tw:w-[156px] tw:!text-sm">
                Back
            </Button>
            <Button onClick={goNext} className="tw:w-[156px] tw:!text-sm" disabled={isUploading || isUpdating}>
                {isUpdating && <LoaderCircle className="tw:size-4 tw:animate-spin" />}
                Next
            </Button>
        </div>
    )
}

const VideoThumbnailScreen = () => {
    const [adAsset, setAdAsset] = useState<AdCreativeAsset | null>(null)
    const [isLoading, setIsLoading] = useState(true)
    const [isSaving, setIsSaving] = useState(false)
    const {
        id: adId,
        campaignId,
        getStepContentData,
        goToStep,
        setStepContentData,
        registerCustomNavigation,
        clearCustomNavigation,
        currentStep,
        stepOrder,
        dataForStepper,
    } = useWizard() as WizardContextType
    const { assetId } = getStepContentData()

    const { refetch: refetchAssets } = useQuery(GET_AD_ASSETS, {
        variables: { campaignId, adId },
        skip: !campaignId || !adId,
    })

    const { handleSaveThumbnail, handleSelectionChange, openThumbnailEditor, isUpdatingThumbnail, ...editor } =
        useThumbnailEditor(refetchAssets)
    const currentSelection = editor.currentSelection as ThumbnailSelection

    useEffect(() => {
        openThumbnailEditor(adAsset)
    }, [adAsset?.id])

    useQuery(GET_AD_ASSET_BY_ID, {
        fetchPolicy: 'network-only',
        variables: { id: assetId },
        skip: !assetId,

        onCompleted: (data) => {
            setAdAsset(data?.AdAsset?.adAsset)
            setIsLoading(false)
        },
    })

    const goBack = () => goToStep('videoTrim')

    // Frames are extracted in the background after upload; until they exist, automatic mode keeps the
    // preview thumbnail the asset already has, so there is nothing to save
    const goNext = async () => {
        const framesNotReady = currentSelection?.mode === 'automatic' && !currentSelection.firstThumbnail

        if (currentSelection?.mode === 'manual' && !currentSelection.selectedThumbnail) {
            showNotification('Please select a thumbnail', 'info')
            return
        }

        setIsSaving(true)

        try {
            if (!framesNotReady) await handleSaveThumbnail()
        } catch (error) {
            setIsSaving(false)
            showNotification((error as Error)?.message || 'Failed to save thumbnail', 'error')
            return
        }

        setStepContentData({ assetId }, 'setMediaCrop')
        goToStep('setMediaCrop')
    }

    const isUploading = currentSelection?.mode === 'uploading'

    // Custom navigation for video thumbnail screen
    useEffect(() => {
        if (!adAsset) return

        registerCustomNavigation(
            <Navigation
                goBack={goBack}
                goNext={goNext}
                isUploading={isUploading}
                isUpdating={isSaving || isUpdatingThumbnail}
            />,
        )

        return () => clearCustomNavigation()
    }, [adAsset, currentStep, stepOrder.length, isUploading, currentSelection, isUpdatingThumbnail, isSaving])

    if (isLoading || !adAsset) {
        return (
            <div className="tw:w-full tw:flex tw:items-center tw:justify-center">
                <CubeLoader size="default" fullScreen={false} />
            </div>
        )
    }

    return (
        <div className="tw:flex tw:h-full">
            <WizardSteps adCreative={dataForStepper} currentStep="setVideoThumbnail" />
            <div className="tw:flex-1 tw:py-4 tw:overflow-auto tw:text-white">
                <div className="tw:text-sm">
                    <VideoThumbnailEditor onSelectionChange={handleSelectionChange} adAsset={adAsset} />
                </div>
            </div>
        </div>
    )
}

export default VideoThumbnailScreen
